/**
 * AppLayout Component
 * Main layout shell for the workspace application.
 * Includes Sidebar navigation + main content area.
 *
 * Ported from v0.26 legacy app -- adapted for verisa_v1 project structure.
 *
 * Layout structure:
 * +---------------------------------------------+
 * | Workspace Layout                             |
 * +----------+----------------------------------+
 * | Sidebar  |  Main Content Area               |
 * | (240px)  |  (flex: 1)                       |
 * |          |                                  |
 * | - Logo   |  <Outlet /> (React Router)       |
 * | - Search |                                  |
 * | - Nav    |                                  |
 * | - User   |                                  |
 * +----------+----------------------------------+
 */

import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Outlet } from 'react-router-dom';
// The width constants are no longer imported: the drawer is in FLOW (RULE-020),
// so nothing outside Sidebar.tsx needs to know how wide it is.
import Sidebar from './Sidebar';
import PlatformNoticeBar from '../shared/PlatformNoticeBar';
import { useBranding } from '../../branding/BrandingProvider';
import { DefaultBranding } from '../../branding/defaults';
import { colors } from '../../theme/colors';
import { AccessibilityDrawer } from '../accessibility';
import { FeatureGate } from '../../feature-gate';
import { useImpersonationShellSignal } from '../../api/impersonation';
import { IMPERSONATION_BANNER_MIN_HEIGHT } from '../shared/ImpersonationBanner';

interface AppLayoutProps {
  /** Optional children to render instead of Outlet */
  children?: React.ReactNode;
  /**
   * The shell's FULL-WIDTH BOTTOM ROW, rendered as the LAST child of the
   * layout's full-height column - under both the navigation and the content
   * (SPEC-PLATFORM-BRANDING RULE-020, D-BR-29, SCENARIO-032).
   *
   * OPTIONAL, and unset the shell renders exactly as it did before the prop
   * existed. It is a prop and not a mount inside this shell because RULE-006a
   * enumerates the surfaces that carry the legal footer and this shell has other
   * consumers; mounting it here would put the bar on surfaces the mount table
   * does not list.
   *
   * Why a prop at all, when the bar used to arrive as a CHILD: a child lands
   * inside the padded content wrapper, which is INSIDE the scrolling `main`
   * region and beside the navigation - the placement RULE-020 supersedes.
   */
  footer?: React.ReactNode;
}

/**
 * AppLayout is the main layout component for the workspace application.
 * It provides:
 * - Responsive sidebar navigation
 * - Mobile app bar with hamburger menu
 * - Main content area with proper spacing
 *
 * Responsive behavior:
 * - Desktop (>= 960px): Full sidebar (240px)
 * - Tablet (600-959px): Collapsed sidebar (60px icons only)
 * - Mobile (< 600px): Hidden sidebar with hamburger menu in app bar
 *
 * User/auth props are no longer needed -- Sidebar reads from AuthContext directly.
 */
const AppLayout: React.FC<AppLayoutProps> = ({ children, footer }) => {
  const { _: lingui } = useLingui();
  // RULE-001, three postures in one value: the provider hands back
  // DefaultBranding both while the bounded read is in flight and after it
  // failed, so those two paint the built-in brand silently; the `||` covers the
  // third - a record that WAS read and carries an empty product name (RULE-016
  // says it never is, and the fallback is the cost of being wrong about that).
  const { branding } = useBranding();
  const productName = branding.product_name || DefaultBranding.product_name;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // NO sidebar-width computation any more. The permanent drawer is laid out in
  // FLOW (Sidebar.tsx, RULE-020 / D-BR-29), so it occupies its own width in the
  // row and the content region takes the rest with `flexGrow`. The old
  // `currentSidebarWidth` existed only to mirror a viewport-FIXED drawer's width
  // back onto the content as a `margin-left` and a `calc()` width - a second
  // copy of the same number that had to be kept in step by hand.

  // THE IMPERSONATION BANNER OFFSET (SPEC-FRONTEND-RULE-095,
  // TASK-IMP-AUTHCONTEXT-SPA-WIRING-CHG-026). This shell has NO persistent
  // desktop bar - the AppBar below renders only on mobile - so the banner's
  // fixed strip gets its own top offset on the flex container, and the fixed
  // pieces (the mobile AppBar, the sidebar drawer paper) move down with it.
  // 0 with no binding, which leaves every value as it was.
  const bannerOffset = useImpersonationShellSignal().bannerShown
    ? IMPERSONATION_BANNER_MIN_HEIGHT
    : 0;
  const bannerOffsetSx = bannerOffset
    ? {
        pt: `${bannerOffset}px`,
        '& .MuiAppBar-positionFixed': { top: `${bannerOffset}px` },
        '& .MuiDrawer-paper': {
          top: `${bannerOffset}px`,
          height: `calc(100% - ${bannerOffset}px)`,
        },
      }
    : {};

  return (
    // PlatformNoticeBar is mounted HERE, in the shared layout, rather than in
    // WorkspaceApp: AppLayout is the single shell every workspace route renders
    // through, so every consumer - including any future workspace surface that
    // reuses this shell - gets the CRA Art. 14(8) notice automatically
    // (SPEC-OPERATIONS RULE-019, TASK-SCR-NOTICE-MOUNT-WORKSPACE). It renders
    // null when no notice is active, so the ordinary layout is unchanged.
    //
    // `height`, NOT `minHeight` (RULE-020: the column that holds the bar does
    // not scroll; the content scrolls in its own region above). A minimum is a
    // floor that grows with the content, so `main` never overflowed and the chat
    // page grew without end after a message (TASK-BUGFIX-WORKSPACE-CHAT-HEIGHT).
    // The tenant shell's column declares the same bound.
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', ...bannerOffsetSx }}>
      <PlatformNoticeBar />
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
      {/* Mobile App Bar - Only shown on mobile */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: colors.background.default,
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: colors.text.primary,
          }}
        >
          <Toolbar
            sx={{
              minHeight: '56px !important',
              px: 2,
            }}
          >
            <IconButton
              color="inherit"
              aria-label={lingui(msg`Open navigation menu`)}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>

            {/* THE INSTALLATION BRAND IN THE MOBILE APP BAR.
                Both values come from the ONE branding reader mounted at the SPA
                root (UI-004, apps/workspace/App.tsx), exactly as the sidebar
                header does (Sidebar.tsx, TASK-PB-BRAND-WORKSPACE-SHELL): this
                shell holds no product name and no logo path of its own
                (RULE-012) and it fetches nothing (SPEC-FRONTEND-RULE-077).
                Until now a phone showed the built-in "V" monogram and the
                literal vendor name on EVERY branded installation - the RULE-012
                literal no census named, found while building the gate.
                The `?? undefined` tail is the nullable CONTRACT-001 type, not a
                third fallback: `src={null}` is not something an <img> can carry;
                the `?? DefaultBranding.logo_url` before it is the third RULE-001
                posture - a record that WAS read and carries no logo. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                component="img"
                src={branding.logo_url ?? DefaultBranding.logo_url ?? undefined}
                alt={productName}
                sx={{
                  width: 28,
                  height: 28,
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: colors.text.primary,
                }}
              >
                {productName}
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <FeatureGate feature="global.accessibility">
              <AccessibilityDrawer />
            </FeatureGate>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar Navigation */}
      <Sidebar
        mobileOpen={mobileOpen}
        onMobileToggle={handleDrawerToggle}
      />

      {/* Main Content Area.
          IT TAKES ITS SIZE FROM THE ROW, NOT FROM THE VIEWPORT (RULE-020,
          D-BR-29). It used to declare `height: 100vh` and offset itself from a
          viewport-fixed sidebar with `ml` and a `calc()` width; the sidebar is
          in flow now (Sidebar.tsx, CHG-005), so `flexGrow: 1` takes exactly the
          width the sidebar leaves, and `flex 1 / minHeight 0` takes exactly the
          height the row has once the bottom bar has taken its own. `100vh` here
          is what would have pushed the bar off the screen. `minWidth: 0` is the
          flex floor that lets a wide child scroll inside this region instead of
          widening the row. The mobile app-bar offset stays: that bar is fixed. */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: 0,
          // Account for mobile app bar height
          mt: {
            xs: '56px',
            sm: 0,
          },
          overflow: 'auto',
          bgcolor: colors.background.default,
        }}
      >
        {/* Content wrapper with padding */}
        <Box
          sx={{
            p: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children || <Outlet />}
        </Box>
      </Box>
      </Box>
      {/* The shell's FULL-WIDTH BOTTOM ROW (RULE-020, D-BR-29, SCENARIO-032):
          the LAST child of the full-height column, a SIBLING of the row above,
          so it spans under BOTH the navigation and the content. Rendering
          `undefined` adds no node, which is how every other consumer of this
          shell keeps the markup it has today. */}
      {footer}
    </Box>
  );
};

export default AppLayout;
